import React, { useState, useEffect } from 'react';
import { FiDownload, FiCalendar, FiTrendingUp, FiUsers, FiCheckCircle, FiClock, FiAlertCircle, FiActivity, FiGrid } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import '../styles/Reports.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Reports = () => {
  const { token } = useAuth();
  const [reportType, setReportType] = useState('weekly');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Productivity state
  const [productivityData, setProductivityData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [productivityLoading, setProductivityLoading] = useState(false);

  // Metrics state (Lead Time, Cycle Time, Workload)
  const [metricsData, setMetricsData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  useEffect(() => {
    fetchReportData();
    fetchProductivityData();
    fetchMetricsData();
  }, [reportType]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await api.get(`/reports/${reportType}`, { params });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductivityData = async () => {
    setProductivityLoading(true);
    try {
      const params = { userId: selectedEmployee };
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await api.get('/reports/productivity', { params });
      setProductivityData(response.data);
    } catch (error) {
      console.error('Error fetching productivity data:', error);
    } finally {
      setProductivityLoading(false);
    }
  };

  useEffect(() => {
    if (productivityData) {
      fetchProductivityData();
    }
  }, [selectedEmployee]);

  const fetchMetricsData = async () => {
    setMetricsLoading(true);
    try {
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await api.get('/reports/metrics', { params });
      setMetricsData(response.data);
    } catch (error) {
      console.error('Error fetching metrics data:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const params = {
        type: reportType
      };
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await api.get('/reports/export/excel', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `task_report_${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert('Failed to download report');
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyDateFilter = () => {
    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      if (start > end) {
        toast.error('Start date cannot be after end date.');
        return;
      }
    }
    fetchReportData();
    fetchProductivityData();
    fetchMetricsData();
  };

  if (loading) {
    return (
      <div className="reports-container">
        <div className="loading">Loading report data...</div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="reports-container">
        <div className="loading">No data available</div>
      </div>
    );
  }

  // Prepare chart data
  const statusLabels = Object.keys(reportData.summary.tasksByStatus || {});
  const statusData = Object.values(reportData.summary.tasksByStatus || {});

  const statusChartData = {
    labels: statusLabels.map(s => s.charAt(0).toUpperCase() + s.slice(1)),
    datasets: [
      {
        label: 'Tasks by Status',
        data: statusData,
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  const urgencyLabels = [];
  const urgencyData = [];
  for (let i = 1; i <= 5; i++) {
    urgencyLabels.push(`Priority ${i}`);
    urgencyData.push(reportData.summary.tasksByUrgency[`urgency${i}`] || 0);
  }

  const urgencyChartData = {
    labels: urgencyLabels,
    datasets: [
      {
        label: 'Tasks by Urgency',
        data: urgencyData,
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(255, 99, 132, 0.7)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  // Trend data
  const trendLabels = reportType === 'weekly'
    ? (reportData.dailyTrend || []).map(d => d._id)
    : (reportData.weeklyTrend || []).map(d => `Week ${d._id.split('-')[1]}`);

  const trendData = reportType === 'weekly'
    ? (reportData.dailyTrend || []).map(d => d.created)
    : (reportData.weeklyTrend || []).map(d => d.created);

  const trendChartData = {
    labels: trendLabels,
    datasets: [
      {
        label: 'Tasks Created',
        data: trendData,
        fill: false,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        tension: 0.1,
        pointBackgroundColor: 'rgba(75, 192, 192, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(75, 192, 192, 1)'
      }
    ]
  };

  // User performance
  const topUsers = (reportData.userPerformance || []).slice(0, 10);
  const userLabels = topUsers.map(u => u.username || 'Unknown');
  const userData = topUsers.map(u => u.tasksCompleted);

  const userPerformanceChartData = {
    labels: userLabels,
    datasets: [
      {
        label: 'Tasks Completed',
        data: userData,
        backgroundColor: 'rgba(153, 102, 255, 0.7)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            family: "'Inter', sans-serif"
          }
        }
      },
      title: {
        display: false
      }
    }
  };

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1><FiTrendingUp /> Reports & Analytics</h1>
        <div className="reports-controls">
          <div className="report-type-selector">
            <button
              className={reportType === 'weekly' ? 'active' : ''}
              onClick={() => setReportType('weekly')}
            >
              Weekly
            </button>
            <button
              className={reportType === 'monthly' ? 'active' : ''}
              onClick={() => setReportType('monthly')}
            >
              Monthly
            </button>
          </div>
          <div className="date-range-selector">
            <FiCalendar />
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              placeholder="Start Date"
            />
            <span>to</span>
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              placeholder="End Date"
            />
            <button onClick={applyDateFilter} className="apply-filter-btn">
              Apply
            </button>
          </div>
          <button onClick={handleDownloadExcel} className="download-excel-btn">
            <FiDownload /> Download Excel
          </button>
        </div>
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon blue">
            <FiCheckCircle />
          </div>
          <div className="card-content">
            <h2>{reportData.summary.totalCreated}</h2>
            <p>Total Tasks Created</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon green">
            <FiCheckCircle />
          </div>
          <div className="card-content">
            <h2>{reportData.summary.totalCompleted}</h2>
            <p>Tasks Completed</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon orange">
            <FiTrendingUp />
          </div>
          <div className="card-content">
            <h2>
              {reportData.summary.totalCreated > 0
                ? Math.round((reportData.summary.totalCompleted / reportData.summary.totalCreated) * 100)
                : 0}%
            </h2>
            <p>Completion Rate</p>
          </div>
        </div>
        {reportData.summary.avgCompletionTimeHours !== undefined && (
          <div className="summary-card">
            <div className="card-icon purple">
              <FiCalendar />
            </div>
            <div className="card-content">
              <h2>{reportData.summary.avgCompletionTimeHours}h</h2>
              <p>Avg. Completion Time</p>
            </div>
          </div>
        )}
      </div>

      <div className="charts-grid">
        <div className="chart-container">
          <h2>Tasks by Status</h2>
          <div className="chart-wrapper">
            <Doughnut data={statusChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-container">
          <h2>Tasks by Priority</h2>
          <div className="chart-wrapper">
            <Pie data={urgencyChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-container full-width">
          <h2>{reportType === 'weekly' ? 'Daily' : 'Weekly'} Task Creation Trend</h2>
          <div className="chart-wrapper">
            <Line data={trendChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-container full-width">
          <h2>Top 10 User Performance (Tasks Completed)</h2>
          <div className="chart-wrapper">
            <Bar data={userPerformanceChartData} options={{
              ...chartOptions,
              indexAxis: 'y',
              plugins: {
                ...chartOptions.plugins,
                legend: {
                  display: false
                }
              }
            }} />
          </div>
        </div>

        {/* Productivity Histogram Section */}
        <div className="chart-container full-width productivity-section">
          <div className="productivity-header">
            <h2><FiClock /> Productivity Analysis (On-Time vs Late)</h2>
            <div className="employee-selector">
              <label>View:</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="employee-dropdown"
              >
                <option value="all">Entire Company</option>
                {productivityData?.users?.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.username}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {productivityLoading ? (
            <div className="loading">Loading productivity data...</div>
          ) : productivityData ? (
            <>
              {/* Summary Stats */}
              <div className="productivity-stats">
                {selectedEmployee === 'all' ? (
                  <>
                    <div className="stat-item on-time">
                      <FiCheckCircle />
                      <span className="stat-value">{productivityData.companyStats?.onTime || 0}</span>
                      <span className="stat-label">On Time</span>
                    </div>
                    <div className="stat-item late">
                      <FiAlertCircle />
                      <span className="stat-value">{productivityData.companyStats?.late || 0}</span>
                      <span className="stat-label">Late</span>
                    </div>
                    <div className="stat-item total">
                      <FiTrendingUp />
                      <span className="stat-value">{productivityData.companyStats?.onTimePercentage || 0}%</span>
                      <span className="stat-label">On-Time Rate</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="stat-item on-time">
                      <FiCheckCircle />
                      <span className="stat-value">{productivityData.selectedUser?.onTime || 0}</span>
                      <span className="stat-label">On Time</span>
                    </div>
                    <div className="stat-item late">
                      <FiAlertCircle />
                      <span className="stat-value">{productivityData.selectedUser?.late || 0}</span>
                      <span className="stat-label">Late</span>
                    </div>
                    <div className="stat-item total">
                      <FiTrendingUp />
                      <span className="stat-value">{Math.round(productivityData.selectedUser?.onTimePercentage || 0)}%</span>
                      <span className="stat-label">On-Time Rate</span>
                    </div>
                  </>
                )}
              </div>

              {/* Histogram Chart */}
              <div className="chart-wrapper">
                <Bar
                  data={{
                    labels: selectedEmployee === 'all'
                      ? (productivityData.userStats || []).map(u => u.username)
                      : ['On Time', 'Late'],
                    datasets: selectedEmployee === 'all'
                      ? [
                        {
                          label: 'On Time',
                          data: (productivityData.userStats || []).map(u => u.onTime),
                          backgroundColor: 'rgba(75, 192, 92, 0.7)',
                          borderColor: 'rgba(75, 192, 92, 1)',
                          borderWidth: 2
                        },
                        {
                          label: 'Late',
                          data: (productivityData.userStats || []).map(u => u.late),
                          backgroundColor: 'rgba(255, 99, 132, 0.7)',
                          borderColor: 'rgba(255, 99, 132, 1)',
                          borderWidth: 2
                        }
                      ]
                      : [
                        {
                          label: 'Tasks',
                          data: [
                            productivityData.selectedUser?.onTime || 0,
                            productivityData.selectedUser?.late || 0
                          ],
                          backgroundColor: [
                            'rgba(75, 192, 92, 0.7)',
                            'rgba(255, 99, 132, 0.7)'
                          ],
                          borderColor: [
                            'rgba(75, 192, 92, 1)',
                            'rgba(255, 99, 132, 1)'
                          ],
                          borderWidth: 2
                        }
                      ]
                  }}
                  options={{
                    ...chartOptions,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        }
                      }
                    },
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: selectedEmployee === 'all'
                      },
                      title: {
                        display: true,
                        text: selectedEmployee === 'all'
                          ? 'Tasks Completed On-Time vs Late by Employee'
                          : 'Task Completion Status'
                      }
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <div className="no-data">No productivity data available</div>
          )}
        </div>

        {/* Lead Time & Cycle Time Section */}
        <div className="chart-container full-width">
          <h2><FiActivity /> Lead Time & Cycle Time (Hours)</h2>
          {metricsLoading ? (
            <div className="loading">Loading metrics...</div>
          ) : metricsData ? (
            <>
              <div className="metrics-summary">
                <div className="metric-card lead-time">
                  <span className="metric-label">Company Avg Lead Time</span>
                  <span className="metric-value">{metricsData.leadTime?.companyAverage || 0}h</span>
                  <span className="metric-desc">Creation → Completion</span>
                </div>
                <div className="metric-card cycle-time">
                  <span className="metric-label">Company Avg Cycle Time</span>
                  <span className="metric-value">{metricsData.cycleTime?.companyAverage || 0}h</span>
                  <span className="metric-desc">Work Started → Completion</span>
                </div>
              </div>
              <div className="chart-wrapper">
                <Bar
                  data={{
                    labels: (metricsData.leadTime?.byUser || []).map(u => u.username),
                    datasets: [
                      {
                        label: 'Lead Time (hrs)',
                        data: (metricsData.leadTime?.byUser || []).map(u => u.avgLeadTime),
                        backgroundColor: 'rgba(54, 162, 235, 0.7)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 2
                      },
                      {
                        label: 'Cycle Time (hrs)',
                        data: (metricsData.cycleTime?.byUser || []).map(u => u.avgCycleTime),
                        backgroundColor: 'rgba(153, 102, 255, 0.7)',
                        borderColor: 'rgba(153, 102, 255, 1)',
                        borderWidth: 2
                      }
                    ]
                  }}
                  options={{
                    ...chartOptions,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Hours'
                        }
                      }
                    },
                    plugins: {
                      ...chartOptions.plugins,
                      tooltip: {
                        callbacks: {
                          afterLabel: (context) => {
                            const user = metricsData.leadTime?.byUser?.[context.dataIndex];
                            return user ? `Tasks: ${user.taskCount}` : '';
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <div className="no-data">No metrics data available</div>
          )}
        </div>

        {/* Workload Heatmap Section */}
        <div className="chart-container full-width">
          <h2><FiGrid /> Workload Heatmap</h2>
          {metricsLoading ? (
            <div className="loading">Loading workload data...</div>
          ) : metricsData?.workloadHeatmap?.length > 0 ? (
            <div className="workload-heatmap">
              <div className="heatmap-header">
                <div className="heatmap-cell header-cell">Developer</div>
                <div className="heatmap-cell header-cell">To Do</div>
                <div className="heatmap-cell header-cell">In Progress</div>
                <div className="heatmap-cell header-cell">Done</div>
                <div className="heatmap-cell header-cell">Total</div>
              </div>
              {metricsData.workloadHeatmap.map((user, idx) => {
                const maxTasks = Math.max(...metricsData.workloadHeatmap.map(u => u.total)) || 1;
                const getHeatColor = (count, type) => {
                  const intensity = count / (maxTasks * 0.5);
                  const capped = Math.min(intensity, 1);
                  if (type === 'todo') return `rgba(255, 206, 86, ${0.2 + capped * 0.6})`;
                  if (type === 'in-progress') return `rgba(54, 162, 235, ${0.2 + capped * 0.6})`;
                  if (type === 'done') return `rgba(75, 192, 92, ${0.2 + capped * 0.6})`;
                  return `rgba(153, 102, 255, ${0.2 + capped * 0.6})`;
                };
                return (
                  <div key={idx} className="heatmap-row">
                    <div className="heatmap-cell user-cell">{user.username}</div>
                    <div
                      className="heatmap-cell data-cell"
                      style={{ backgroundColor: getHeatColor(user.todo, 'todo') }}
                    >
                      {user.todo}
                    </div>
                    <div
                      className="heatmap-cell data-cell"
                      style={{ backgroundColor: getHeatColor(user['in-progress'], 'in-progress') }}
                    >
                      {user['in-progress']}
                    </div>
                    <div
                      className="heatmap-cell data-cell"
                      style={{ backgroundColor: getHeatColor(user.done, 'done') }}
                    >
                      {user.done}
                    </div>
                    <div
                      className="heatmap-cell data-cell total-cell"
                      style={{ backgroundColor: getHeatColor(user.total, 'total') }}
                    >
                      {user.total}
                    </div>
                  </div>
                );
              })}
              <div className="heatmap-legend">
                <span>Lower workload</span>
                <div className="legend-gradient"></div>
                <span>Higher workload</span>
              </div>
            </div>
          ) : (
            <div className="no-data">No workload data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
